import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Terminal, Trash2 } from 'lucide-react';
import type { ToolEvent } from '@itr/shared';
import { PORT_PRESETS } from '@itr/shared';
import { api, streamNdjson } from '../api/client';
import { playAlertChime } from '../notifications/chime';
import { Badge, useToast } from './ui';

/**
 * The diagnostics runner, minus the target.
 *
 * Shared by the standalone Diagnostics page — where the operator types an
 * address — and by the per-resource panel, where the server already knows what
 * to aim at. Only the input differs; the streaming, the transcript and the
 * result tables are identical, and an engineer reading a ping on a resource
 * page should see exactly what they'd see on the tools page.
 */

export type DiagnosticTool = 'ping' | 'traceroute' | 'port-scan' | 'sweep' | 'dns' | 'http' | 'lldp';

/**
 * A continuous ping runs for hours, so its transcript and result rows cannot
 * grow without bound. Set well clear of the largest finite run any tool
 * produces (a /22 sweep and a full port-scan range both top out near 1024),
 * so nothing that does terminate is ever truncated.
 */
const MAX_RETAINED = 5000;

function capped<T>(rows: T[]): T[] {
  return rows.length > MAX_RETAINED ? rows.slice(rows.length - MAX_RETAINED) : rows;
}

/** Tools that stream NDJSON; the rest answer with a single JSON body. */
export const STREAMING_TOOLS: DiagnosticTool[] = ['ping', 'traceroute', 'port-scan', 'sweep'];

export interface DiagnosticRun {
  running: boolean;
  lines: string[];
  items: Record<string, unknown>[];
  summary: Record<string, unknown> | null;
  progress: { done: number; total: number } | null;
  error: string | null;
  /** POST to `path` and collect whatever comes back. */
  start: (path: string, body: unknown, streaming: boolean) => Promise<void>;
  stop: () => void;
  reset: () => void;
  clearLines: () => void;
}

export function useDiagnosticRun(): DiagnosticRun {
  const toast = useToast();
  const [running, setRunning] = useState(false);
  const [lines, setLines] = useState<string[]>([]);
  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  const [summary, setSummary] = useState<Record<string, unknown> | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setLines([]);
    setItems([]);
    setSummary(null);
    setProgress(null);
    setError(null);
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setRunning(false);
  }, []);

  // A run that outlives its panel keeps the server working for nobody.
  useEffect(() => () => abortRef.current?.abort(), []);

  const start = useCallback(
    async (path: string, body: unknown, streaming: boolean) => {
      reset();
      setRunning(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        if (streaming) {
          await streamNdjson<ToolEvent>(
            path,
            body,
            (event) => {
              switch (event.type) {
                case 'line':
                  setLines((current) => capped([...current, event.text]));
                  break;
                case 'item':
                  setItems((current) => capped([...current, event.item]));
                  break;
                case 'progress':
                  setProgress({ done: event.done, total: event.total });
                  break;
                case 'done':
                  setSummary(event.summary ?? null);
                  if (!event.ok && event.error) setError(event.error);
                  break;
              }
            },
            controller.signal,
          );
        } else {
          setSummary(await api.post<Record<string, unknown>>(path, body));
        }
      } catch (err) {
        // An abort is the operator's own click, not a failure worth reporting.
        if ((err as Error).name !== 'AbortError') {
          const message = err instanceof Error ? err.message : String(err);
          setError(message);
          toast('Diagnostic failed', 'bad', message);
        }
      } finally {
        setRunning(false);
        abortRef.current = null;
      }
    },
    [reset, toast],
  );

  return { running, lines, items, summary, progress, error, start, stop, reset, clearLines: () => setLines([]) };
}

/**
 * Watches a live ping trace and announces every change of state — replies
 * starting, and replies stopping — with a toast and a chime.
 *
 * The point of a continuous trace is that nobody is reading every line of it:
 * an engineer starts one against a flapping link and goes back to their other
 * window. Only the transitions matter, so only the transitions interrupt; a
 * thousand consecutive good replies say nothing new and stay silent.
 *
 * The first reading establishes the baseline rather than firing — starting a
 * trace against an already-dead host should not announce "stopped replying"
 * as though something just happened.
 */
export function usePingTransitionAlerts(run: DiagnosticRun, enabled: boolean, label: string): void {
  const toast = useToast();
  const replyingRef = useRef<boolean | null>(null);
  const seenRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      replyingRef.current = null;
      seenRef.current = 0;
      return;
    }

    // A fresh run resets the item list; start the baseline over with it.
    if (run.items.length < seenRef.current) {
      replyingRef.current = null;
      seenRef.current = 0;
    }

    for (const item of run.items.slice(seenRef.current)) {
      const replying = item.timeout !== true;
      if (replyingRef.current === null) {
        replyingRef.current = replying;
        continue;
      }
      if (replying === replyingRef.current) continue;

      replyingRef.current = replying;
      playAlertChime(replying ? 'up' : 'down');
      toast(
        replying ? `${label} is replying again` : `${label} stopped replying`,
        replying ? 'good' : 'bad',
      );
    }
    seenRef.current = run.items.length;
  }, [run.items, enabled, label, toast]);
}

/* ================================================================== */
/* Output                                                             */
/* ================================================================== */

export function DiagnosticProgress({ run }: { run: DiagnosticRun }) {
  if (!run.progress || run.progress.total <= 1) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div className="bar-track" style={{ height: 5 }}>
        <div className="bar-fill" style={{ width: `${(run.progress.done / run.progress.total) * 100}%`, background: 'var(--itr-primary)' }} />
      </div>
      <div className="subtle nums" style={{ fontSize: 11.5, marginTop: 4 }}>
        {run.progress.done} / {run.progress.total}
      </div>
    </div>
  );
}

/** Error, summary, result table and transcript — everything a run produces. */
export function DiagnosticResults({ tool, run }: { tool: DiagnosticTool; run: DiagnosticRun }) {
  return (
    <>
      {run.error && (
        <div
          className="card"
          style={{ marginBottom: 14, padding: '11px 14px', borderLeft: '3px solid var(--itr-critical)', color: 'var(--itr-critical)', fontSize: 13 }}
        >
          {run.error}
        </div>
      )}

      {run.summary && <SummaryStrip summary={run.summary} />}

      {run.items.length > 0 && <ResultTable tool={tool} items={run.items} />}

      {(run.lines.length > 0 || run.running) && <OutputConsole run={run} />}
    </>
  );
}

function OutputConsole({ run }: { run: DiagnosticRun }) {
  const logRef = useRef<HTMLPreElement>(null);

  // Pin to the bottom while output streams, the way a terminal does.
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [run.lines.length]);

  return (
    <div className="card">
      <div className="card-header">
        <Terminal size={14} />
        <span className="card-title">Output</span>
        <span className="grow" />
        {run.running && <span className="spinner" />}
        <button className="btn btn-ghost btn-icon btn-sm" onClick={run.clearLines} title="Clear" aria-label="Clear output">
          <Trash2 size={13} />
        </button>
      </div>
      <pre
        ref={logRef}
        className="mono"
        style={{
          margin: 0,
          padding: '12px 16px',
          maxHeight: 380,
          overflow: 'auto',
          fontSize: 12,
          lineHeight: 1.65,
          background: 'var(--itr-surface-sunken)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {run.lines.join('\n')}
      </pre>
    </div>
  );
}

export function SummaryStrip({ summary }: { summary: Record<string, unknown> }) {
  const entries = Object.entries(summary).filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object');
  if (entries.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <pre className="mono" style={{ margin: 0, padding: 14, fontSize: 12, overflow: 'auto', maxHeight: 420 }}>
          {JSON.stringify(summary, null, 2)}
        </pre>
      </div>
    );
  }

  return (
    <div className="stat-strip">
      {entries.map(([key, value]) => (
        <div className="stat-chip" key={key}>
          <div className="stat-chip-label">{humanise(key)}</div>
          <div
            className="stat-chip-value"
            style={{ fontSize: typeof value === 'string' && value.length > 12 ? 14 : undefined, color: intentFor(key, value) }}
            // An HTTP probe returns a two-thousand character body preview; the
            // whole strip is unreadable if one chip is a page of HTML.
            title={typeof value === 'string' && value.length > 140 ? value : undefined}
          >
            {typeof value === 'string' && value.length > 140 ? `${value.slice(0, 140)}…` : String(value)}
          </div>
        </div>
      ))}
    </div>
  );
}

function intentFor(key: string, value: unknown): string | undefined {
  const n = Number(value);
  if (key === 'lossPct' && Number.isFinite(n)) return n > 0 ? 'var(--itr-critical)' : 'var(--itr-success)';
  if (key === 'proxyArp' && Number.isFinite(n) && n > 0) return 'var(--itr-warning)';
  if (key === 'open' && Number.isFinite(n) && n > 0) return 'var(--itr-warning)';
  if (key === 'ok') return value ? 'var(--itr-success)' : 'var(--itr-critical)';
  return undefined;
}

export function ResultTable({ tool, items }: { tool: DiagnosticTool; items: Record<string, unknown>[] }) {
  const columns = useMemo(() => {
    switch (tool) {
      case 'ping':
        return [
          { key: 'seq', label: '#' },
          { key: 'from', label: 'From' },
          { key: 'rttMs', label: 'RTT (ms)', align: 'right' as const },
          { key: 'ttl', label: 'TTL', align: 'right' as const },
        ];
      case 'traceroute':
        return [
          { key: 'hop', label: 'Hop' },
          { key: 'host', label: 'Host' },
          { key: 'ip', label: 'Address' },
          { key: 'rttMs', label: 'RTT (ms)' },
        ];
      case 'port-scan':
        return [
          { key: 'port', label: 'Port' },
          { key: 'service', label: 'Service' },
          { key: 'connectMs', label: 'Connect (ms)', align: 'right' as const },
          { key: 'banner', label: 'Banner' },
        ];
      case 'sweep':
        return [
          { key: 'ip', label: 'Address' },
          { key: 'hostname', label: 'Hostname' },
          { key: 'mac', label: 'MAC' },
          { key: 'vendor', label: 'Vendor' },
          { key: 'via', label: 'Found via' },
        ];
      default:
        return Object.keys(items[0] ?? {}).map((key) => ({ key, label: humanise(key), align: undefined }));
    }
  }, [tool, items]);

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <span className="card-title">Results</span>
        <span className="card-subtitle" style={{ marginLeft: 'auto' }}>
          {items.length}
        </span>
      </div>
      <div className="table-wrap" style={{ maxHeight: 420 }}>
        <table className="table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} data-align={c.align}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} style={item.proxyArp ? { opacity: 0.55 } : undefined}>
                {columns.map((c) => (
                  <td key={c.key} data-align={c.align}>
                    <ResultCell columnKey={c.key} item={item} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResultCell({ columnKey, item }: { columnKey: string; item: Record<string, unknown> }) {
  const value = item[columnKey];

  if (columnKey === 'rttMs' && Array.isArray(value)) {
    return <span className="nums">{value.map((v) => (v === null ? '*' : v)).join('  ')}</span>;
  }
  if (columnKey === 'via') {
    return (
      <span className="row" style={{ gap: 6 }}>
        <Badge intent={value === 'icmp' ? 'good' : value === 'tcp' ? 'info' : 'neutral'}>{String(value)}</Badge>
        {/* An ARP-only hit on a widely-shared MAC is the router answering for
            the whole subnet, not a host — say so rather than inflating the count. */}
        {item.proxyArp ? <Badge intent="warn">proxy ARP</Badge> : null}
      </span>
    );
  }
  if (columnKey === 'service' && value) return <Badge>{String(value)}</Badge>;
  if (value === null || value === undefined || value === '') return <span className="subtle">—</span>;
  if (typeof value === 'boolean') return <Badge intent={value ? 'good' : 'neutral'}>{String(value)}</Badge>;
  if (columnKey === 'banner') return <span className="mono truncate" style={{ maxWidth: 320, display: 'inline-block' }}>{String(value)}</span>;
  if (columnKey === 'mac' || columnKey === 'ip') return <span className="mono">{String(value)}</span>;
  return <span className={typeof value === 'number' ? 'nums' : undefined}>{String(value)}</span>;
}

/* ================================================================== */
/* Per-tool option controls                                           */
/* ================================================================== */

export function ToolOptions({
  tool,
  options,
  onChange,
}: {
  tool: DiagnosticTool;
  options: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const set = (key: string, value: unknown) => onChange({ ...options, [key]: value });

  if (tool === 'ping') {
    const continuous = Boolean(options.continuous);
    return (
      <>
        {!continuous && (
          <div>
            <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
              Count
            </label>
            <input
              className="input"
              type="number"
              min={1}
              max={100}
              style={{ width: 90 }}
              value={String(options.count ?? 5)}
              onChange={(e) => set('count', Number(e.target.value))}
            />
          </div>
        )}
        <label className="row" style={{ gap: 7, cursor: 'pointer', fontSize: 12.5, height: 34 }}>
          <input type="checkbox" checked={continuous} onChange={(e) => set('continuous', e.target.checked)} />
          <span title="Ping until you stop it, with a chime and a toast whenever replies start or stop.">
            Live trace
          </span>
        </label>
      </>
    );
  }

  if (tool === 'traceroute') {
    return (
      <div>
        <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
          Max hops
        </label>
        <input
          className="input"
          type="number"
          min={1}
          max={40}
          style={{ width: 100 }}
          value={String(options.maxHops ?? 30)}
          onChange={(e) => set('maxHops', Number(e.target.value))}
        />
      </div>
    );
  }

  if (tool === 'port-scan') {
    return (
      <>
        <div>
          <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
            Ports
          </label>
          <select className="select" style={{ width: 190 }} value={String(options.preset ?? 'common')} onChange={(e) => set('preset', e.target.value)}>
            {Object.entries(PORT_PRESETS).map(([key, preset]) => (
              <option key={key} value={key}>
                {preset.label} ({preset.ports.length})
              </option>
            ))}
          </select>
        </div>
        <label className="row" style={{ gap: 7, cursor: 'pointer', fontSize: 12.5, height: 34 }}>
          <input type="checkbox" checked={Boolean(options.grabBanner)} onChange={(e) => set('grabBanner', e.target.checked)} />
          Grab banners
        </label>
      </>
    );
  }

  if (tool === 'sweep') {
    return (
      <label className="row" style={{ gap: 7, cursor: 'pointer', fontSize: 12.5, height: 34 }}>
        <input type="checkbox" checked={options.tcpFallback !== false} onChange={(e) => set('tcpFallback', e.target.checked)} />
        <span title="Many hosts drop ICMP but still answer TCP — without this a live subnet can look empty.">TCP fallback</span>
      </label>
    );
  }

  if (tool === 'dns') {
    return (
      <>
        <div>
          <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
            Type
          </label>
          <select className="select" style={{ width: 100 }} value={String(options.type ?? 'A')} onChange={(e) => set('type', e.target.value)}>
            {['A', 'AAAA', 'CNAME', 'MX', 'NS', 'TXT', 'SOA', 'SRV', 'PTR', 'ANY'].map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
            Resolver
          </label>
          <input
            className="input"
            style={{ width: 150 }}
            placeholder="system"
            value={String(options.server ?? '')}
            onChange={(e) => set('server', e.target.value || undefined)}
          />
        </div>
      </>
    );
  }

  if (tool === 'lldp') {
    return (
      <div>
        <label className="field-label" style={{ display: 'block', marginBottom: 5 }}>
          Community
        </label>
        <input className="input" style={{ width: 150 }} value={String(options.community ?? 'public')} onChange={(e) => set('community', e.target.value)} />
      </div>
    );
  }

  return null;
}

export function humanise(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/_/g, ' ');
}
